import api from "./api";
import { ApiPage, getAll } from "./pagination";

export interface LoginCredentials {
  username: string;
  password: string;
}

export type UserType =
  | "admin"
  | "director"
  | "principal"
  | "head_teacher"
  | "director_of_studies"
  | "head_of_department"
  | "class_teacher"
  | "teacher"
  | "school_doctor"
  | "hostel_matron"
  | "hostel_custodian"
  | "kitchen_manager"
  | "cafeteria_manager"
  | "sports_master"
  | "uniform_manager"
  | "inventory_manager"
  | "school_counsellor"
  | "librarian"
  | "cfo"
  | "bursar"
  | "transport_manager"
  | "bus_driver"
  | "disciplinary_committee"
  | "board_viewer"
  | "donor_viewer"
  | "parent"
  | "student";

/** Filters the account directory accepts; empty values are left out. */
export type ManagedUserFilters = {
  q?: string;
  /** One role, or several separated by commas. */
  role?: string;
  status?: "active" | "inactive";
  account_type?: "staff" | "student" | "parent";
  ordering?: "newest" | "oldest" | "name" | "-name" | "username";
};

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  user_type: UserType;
  phone_number: string | null;
  profile_picture: string | null;
  is_verified: boolean;
  is_active: boolean;
  must_change_password: boolean;
  date_joined: string;
  /** Only present on the caller's own user object (login response, /auth/me/) --
   * resolved role-default + per-user-override capabilities. Absent on other
   * users' records (e.g. the managed-users directory) to avoid an N+1 on the
   * backend. */
  capabilities?: string[];
  /** Same caller-only scoping as capabilities: the department(s) this user is
   * Department.head of, e.g. so "Head of Department" in the header can say
   * which one. */
  headed_departments?: Array<{ id: number; name: string }>;
}

export interface Capability {
  id: number;
  slug: string;
  name: string;
  module: string;
  description: string;
  default_user_types: UserType[];
}

export interface UserCapabilityGrant {
  id: number;
  user: number;
  capability: number;
  capability_slug: string;
  capability_name: string;
  is_revoked: boolean;
  reason: string;
  granted_by: number;
  granted_by_name: string;
  granted_at: string;
  revoked_by: number | null;
  revoked_by_name: string | null;
  revoked_at: string | null;
}

export interface AuthResponse {
  user: User;
  access: string;
  refresh: string;
}

export interface ProvisionUserData {
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  user_type: UserType;
  phone_number?: string;
}

/** What the server proposes for a name: the free login, and the address that
 * comes with it. Both are decided there, since only the directory knows
 * which logins are already taken. */
export interface UsernameSuggestion {
  username: string;
  email: string;
  email_domain: string;
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post("/auth/login/", credentials);
    return response.data;
  },

  async register(userData: ProvisionUserData): Promise<{ user: User }> {
    const response = await api.post("/auth/register/", userData);
    return response.data;
  },

  async suggestUsername(
    first_name: string,
    last_name: string,
  ): Promise<UsernameSuggestion> {
    const response = await api.get<UsernameSuggestion>(
      "/auth/username-suggestion/",
      { params: { first_name, last_name } },
    );
    return response.data;
  },

  async getManagedUsers(
    params?: ManagedUserFilters,
  ): Promise<User[]> {
    return getAll<User>("/auth/managed-users/", params);
  },

  async getManagedUsersPage(
    params: ManagedUserFilters & { page: number; page_size?: number },
  ): Promise<{ results: User[]; count: number }> {
    const response = await api.get<ApiPage<User>>("/auth/managed-users/", {
      params,
    });
    return {
      results: response.data.results,
      count: response.data.count ?? response.data.results.length,
    };
  },

  async setManagedUserStatus(
    id: number,
    is_active: boolean,
    reason: string,
  ): Promise<User> {
    const response = await api.patch(`/auth/managed-users/${id}/status/`, {
      is_active,
      reason,
    });
    return response.data;
  },

  async updateManagedUser(
    id: number,
    // No email: the address belongs to the username and is set from it.
    data: Pick<
      User,
      "first_name" | "last_name" | "phone_number" | "user_type"
    > & { reason: string },
  ): Promise<User> {
    return (await api.patch(`/auth/managed-users/${id}/`, data)).data;
  },

  async resetManagedUserPassword(
    id: number,
    temporary_password: string,
    reason: string,
  ): Promise<void> {
    await api.post(`/auth/managed-users/${id}/reset-password/`, {
      temporary_password,
      reason,
    });
  },

  async logout(): Promise<void> {
    const refreshToken = localStorage.getItem("auth-refresh-token");
    if (refreshToken) {
      try {
        await api.post("/auth/logout/", { refresh: refreshToken });
      } catch (error) {
        // Logout error - silently fail
      }
    }
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get("/auth/me/");
    return response.data;
  },

  async changePassword(passwordData: {
    old_password: string;
    new_password: string;
    new_password_confirm: string;
  }): Promise<void> {
    const response = await api.post("/auth/change-password/", passwordData);
    // Changing the password ends every earlier session, this one included,
    // so carry on with the new tokens it returns.
    if (response?.data?.access) localStorage.setItem("auth-token", response.data.access);
    if (response?.data?.refresh) localStorage.setItem("auth-refresh-token", response.data.refresh);
  },

  // Signed out: asks for an emailed link, and sets the password from it.
  async forgotPassword(email: string): Promise<void> {
    await api.post("/auth/forgot-password/", { email });
  },

  async resetPassword(token: string, new_password: string): Promise<void> {
    await api.post("/auth/reset-password/", { token, new_password });
  },

  async getCapabilities(): Promise<Capability[]> {
    const response = await api.get("/auth/capabilities/");
    return response.data;
  },

  async setRoleCapability(
    capability: number,
    user_type: UserType,
    enabled: boolean,
  ): Promise<Capability> {
    const response = await api.post("/auth/capabilities/role-grants/", {
      capability,
      user_type,
      enabled,
    });
    return response.data;
  },

  async getUserCapabilityGrants(userId: number): Promise<{
    grants: UserCapabilityGrant[];
    effective_capabilities: string[];
  }> {
    const response = await api.get(
      `/auth/users/${userId}/capability-grants/`,
    );
    return response.data;
  },

  async setUserCapabilityGrant(
    userId: number,
    capability: number,
    is_revoked: boolean,
    reason: string,
  ): Promise<UserCapabilityGrant> {
    const response = await api.post(
      `/auth/users/${userId}/capability-grants/set/`,
      { capability, is_revoked, reason },
    );
    return response.data;
  },

  async removeUserCapabilityGrant(
    userId: number,
    grantId: number,
  ): Promise<void> {
    await api.delete(`/auth/users/${userId}/capability-grants/${grantId}/`);
  },
};
